import { createElement, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus, StyleSheet, View } from 'react-native';
import {
  Colors,
  Flex,
  IconHeartFilled,
  IconHeartOutline,
  IconPlayarrowFilled,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { VideoGoodsItemModel } from './types';
import { isWeb, isNotNil } from '../../../../utils';
import { useThrottle } from '../../../../hooks';

// react-native-video v7 네이티브 전용
const RNVideo = !isWeb ? require('react-native-video') : null;

// 좋아요 버튼 — 네이티브 동일: 애니메이션 없이 상태만 토글
const LikeButton = ({
  isLike,
  onPressLike,
}: {
  isLike: boolean;
  onPressLike: () => void;
}) => {
  const handlePress = useThrottle(onPressLike);

  return (
    <Flex position="absolute" top={6} right={6}>
      <Flex onPress={handlePress} padding={{ top: 6, bottom: 6, left: 6, right: 6 }}>
        {isLike ? (
          <IconHeartFilled width={20} height={20} fill={Colors.content_pink_primary} />
        ) : (
          <IconHeartOutline width={20} height={20} fill="#FFFFFF" />
        )}
      </Flex>
    </Flex>
  );
};

type VideoGoodsCardProps = {
  item: VideoGoodsItemModel;
  width: number;
  isLike: boolean;
  isVisible?: boolean;
  onPress: () => void;
  onPressLike: () => void;
};

export const VideoGoodsCard = ({
  item,
  width,
  isLike,
  isVisible = true,
  onPress,
  onPressLike,
}: VideoGoodsCardProps) => {
  const videoHeight = width * item.video.ratio;
  const thumbnailHeight = 56;
  const thumbnailWidth = item.image ? thumbnailHeight / item.image.ratio : 0;
  // 상품정보 컨테이너 width — 웹 flexbox가 자식 min-content로 밀리는 현상 방지 위해 명시적 계산
  // 카드 좌우 padding 8+8=16, 썸네일 width, 썸네일-텍스트 gap 8
  const infoWidth = width - 16 - (item.image ? thumbnailWidth + 8 : 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const throttledPress = useThrottle(onPress);

  return (
    <Flex onPress={throttledPress}>
      {/* 비디오 영역 */}
      <Flex width={width} height={videoHeight} backgroundColor="black" overflow="hidden">
        {/* 프리뷰 이미지 — 재생 전 표시 */}
        {!isPlaying && (
          <RemoteImage
            uri={item.video.preview_image_url}
            width={width}
            height={videoHeight}
            resizeMode="contain"
          />
        )}

        <AutoPlayVideo
          videoUrl={item.video.url}
          isVisible={isVisible}
          onPlaying={() => setIsPlaying(true)}
          onLoad={(d) => setDuration(d)}
        />

        {/* 재생시간 배지 */}
        <Flex position="absolute" top={5} left={5}>
          <Flex.Row
            alignItems="center"
            borderRadius={2}
            backgroundColorValue="rgba(0, 0, 0, 0.8)"
            padding={{ top: 3, bottom: 3, left: 5, right: 7 }}
          >
            <IconPlayarrowFilled width={10} height={10} fill="#FFFFFF" />
            <Flex width={1} />
            <Typography type="body5" color="content_always_white">
              {formatDuration(duration)}
            </Typography>
          </Flex.Row>
        </Flex>

        {/* 좋아요 버튼 */}
        <LikeButton isLike={isLike} onPressLike={onPressLike} />

        {/* 이미지 배지 */}
        {item.image_badge_list && item.image_badge_list.length > 0 && (
          <Flex position="absolute" bottom={6} left={6}>
            <Flex.Row gap={2}>
              {item.image_badge_list.map((badge, index) => (
                <RemoteImage
                  key={index}
                  uri={badge.image_url}
                  width={badge.image_width ?? 44}
                  height={badge.image_height ?? 18}
                  resizeMode="contain"
                />
              ))}
            </Flex.Row>
          </Flex>
        )}
      </Flex>

      {/* 하단 상품 정보 영역 */}
      <Flex.Row padding={{ top: 8, bottom: 16, left: 8, right: 8 }} gap={8}>
        {/* 상품 썸네일 */}
        {item.image && (
          <Flex
            height={thumbnailHeight}
            width={thumbnailWidth}
            borderWidth={0.5}
            borderColorValue="rgba(0, 0, 0, 0.08)"
            overflow="hidden"
          >
            <RemoteImage
              uri={item.image.url}
              width={thumbnailWidth}
              height={thumbnailHeight}
            />
          </Flex>
        )}

        {/* 가격/마켓/상품명 — 명시적 width로 웹 flex 밀림 방지 */}
        <Flex width={infoWidth}>
          <Flex.Row alignItems="center" gap={2}>
            {isNotNil(item.discount_rate) && item.discount_rate > 0 && (
              <Typography type="subtitle1" color="content_pink_primary">
                {item.discount_rate}%
              </Typography>
            )}
            <Typography type="subtitle1" color="content_primary">
              {item.price.toLocaleString()}원
            </Typography>
          </Flex.Row>
          {isNotNil(item.market_name) && (
            <Typography type="subtitle4" color="content_secondary">
              {item.market_name}
            </Typography>
          )}
          {isNotNil(item.name) && (
            <Typography
              type="body4"
              color="content_secondary"
              numberOfLines={1}
            >
              {item.name}
            </Typography>
          )}
        </Flex>
      </Flex.Row>
    </Flex>
  );
};

type FullVideoGoodsCardProps = {
  item: VideoGoodsItemModel;
  width: number;
  isLike: boolean;
  isVisible?: boolean;
  onPress: () => void;
  onPressLike: () => void;
};

export const FullVideoGoodsCard = ({
  item,
  width,
  isLike,
  isVisible = true,
  onPress,
  onPressLike,
}: FullVideoGoodsCardProps) => {
  const videoHeight = width * item.video.ratio;
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const throttledPress = useThrottle(onPress);

  return (
    <Flex onPress={throttledPress}>
      <Flex width={width} height={videoHeight} backgroundColor="black" overflow="hidden">
        {/* 프리뷰 이미지 */}
        {!isPlaying && (
          <RemoteImage
            uri={item.video.preview_image_url}
            width={width}
            height={videoHeight}
            resizeMode="contain"
          />
        )}

        <AutoPlayVideo
          videoUrl={item.video.url}
          isVisible={isVisible}
          onPlaying={() => setIsPlaying(true)}
          onLoad={(d) => setDuration(d)}
        />

        {/* 재생시간 배지 */}
        <Flex position="absolute" top={5} left={5}>
          <Flex.Row
            alignItems="center"
            borderRadius={2}
            backgroundColorValue="rgba(0, 0, 0, 0.8)"
            padding={{ top: 3, bottom: 3, left: 5, right: 7 }}
          >
            <IconPlayarrowFilled width={10} height={10} fill="#FFFFFF" />
            <Flex width={1} />
            <Typography type="body5" color="content_always_white">
              {formatDuration(duration)}
            </Typography>
          </Flex.Row>
        </Flex>

        {/* 좋아요 버튼 */}
        <LikeButton isLike={isLike} onPressLike={onPressLike} />
      </Flex>
      {/* iOS: videoHeight + 4 하단 패딩 */}
      <Flex height={4} />
    </Flex>
  );
};

// 플랫폼 통합 비디오 플레이어 — 네이티브/웹 분기를 내부에서 처리
const AutoPlayVideo = ({
  videoUrl,
  isVisible,
  onPlaying,
  onLoad,
}: {
  videoUrl: string;
  isVisible?: boolean;
  onPlaying: () => void;
  onLoad: (duration: number) => void;
}) => {
  if (isWeb) {
    return <WebVideoPlayer videoUrl={videoUrl} onPlaying={onPlaying} onLoad={onLoad} />;
  }
  if (!RNVideo) return null;
  return (
    <VideoPlayer
      videoUrl={videoUrl}
      isVisible={isVisible ?? true}
      onPlaying={onPlaying}
      onLoad={onLoad}
    />
  );
};

// 비디오 플레이어 — 자동재생, 무한루프, 음소거, 가시성/백그라운드 제어
// VideoView는 네이티브 뷰라 View + StyleSheet.absoluteFill 사용 불가피
type VideoPlayerProps = {
  videoUrl: string;
  isVisible: boolean;
  onPlaying: () => void;
  onLoad: (duration: number) => void;
};

const VideoPlayer = ({
  videoUrl,
  isVisible,
  onPlaying,
  onLoad,
}: VideoPlayerProps) => {
  const Video = RNVideo!;
  const onPlayingRef = useRef(onPlaying);
  onPlayingRef.current = onPlaying;
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  const playerRef = useRef<ReturnType<typeof Video.useVideoPlayer> | null>(null);

  const player = Video.useVideoPlayer(
    { uri: videoUrl },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (p: Record<string, any>) => {
      p.muted = true;
      p.loop = true;
      p.play();
    },
  );
  playerRef.current = player;

  // 화면 가시성 기반 재생/정지
  useEffect(() => {
    if (!playerRef.current) return;
    if (isVisible) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  }, [isVisible]);

  // 앱 백그라운드 시 정지, 포그라운드 복귀 시 재개
  useEffect(() => {
    const handleAppState = (nextState: AppStateStatus) => {
      if (!playerRef.current) return;
      if (nextState === 'active' && isVisible) {
        playerRef.current.play();
      } else if (nextState === 'background' || nextState === 'inactive') {
        playerRef.current.pause();
      }
    };
    const sub = AppState.addEventListener('change', handleAppState);
    return () => sub.remove();
  }, [isVisible]);

  // 이벤트 리스너
  useEffect(() => {
    const playbackSub = player.addEventListener(
      'onPlaybackStateChange',
      (data: { isPlaying: boolean }) => {
        if (data.isPlaying) {
          onPlayingRef.current();
        }
      },
    );
    const loadSub = player.addEventListener(
      'onLoad',
      (data: { duration: number }) => onLoadRef.current(data.duration),
    );
    const progressSub = player.addEventListener(
      'onProgress',
      (_data: { currentTime: number }) => {
        // HLS 등 onLoad가 안 오는 경우 duration 폴백
        const dur = player.duration;
        if (dur && !isNaN(dur) && dur > 0) {
          onLoadRef.current(dur);
        }
      },
    );
    const readySub = player.addEventListener(
      'onReadyToDisplay',
      () => {
        const dur = player.duration;
        if (dur && !isNaN(dur) && dur > 0) {
          onLoadRef.current(dur);
        }
      },
    );
    return () => {
      playbackSub.remove();
      loadSub.remove();
      progressSub.remove();
      readySub.remove();
    };
  }, [player]);

  const VideoViewComponent = Video.VideoView;
  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <VideoViewComponent
        player={player}
        style={StyleSheet.absoluteFill}
        resizeMode="cover"
        // Android: 기본 'surface' 사용 시 한 화면에 여러 VideoView 인스턴스 간
        // SurfaceView compositor 충돌 → 같은 영상이 영역 나뉘어 렌더되는 증상 발생.
        // 'texture'(TextureView)로 변경해 각 인스턴스 독립 렌더 보장.
        // 단, TextureView에서 resizeMode="cover"가 완벽히 작동하지 않아 가로형 영상 시
        // 위아래 레터박스 발생 가능. 실서비스에서는 세로형 영상이 내려올 것으로 기대.
        surfaceType="texture"
      />
    </View>
  );
};

// 웹 전용 비디오 플레이어 — HTML5 <video> 엘리먼트
// react-native에선 video 태그가 없으므로 createElement로 렌더 (isWeb 가드로 실제로는 웹에서만 실행)
type WebVideoPlayerProps = {
  videoUrl: string;
  onPlaying: () => void;
  onLoad: (duration: number) => void;
};

const WebVideoPlayer = ({ videoUrl, onPlaying, onLoad }: WebVideoPlayerProps) => {
  const ensurePlaying = (v: HTMLVideoElement) => {
    if (v.paused) {
      v.play().catch(() => {});
    }
  };

  return createElement('video', {
    src: videoUrl,
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    preload: 'auto',
    onPlay: onPlaying,
    onLoadedMetadata: (e: { currentTarget: { duration: number } }) =>
      onLoad(e.currentTarget.duration),
    onCanPlay: (e: { currentTarget: HTMLVideoElement }) => ensurePlaying(e.currentTarget),
    onPause: (e: { currentTarget: HTMLVideoElement }) => ensurePlaying(e.currentTarget),
    onEnded: (e: { currentTarget: HTMLVideoElement }) => {
      const v = e.currentTarget;
      v.currentTime = 0;
      ensurePlaying(v);
    },
    onStalled: (e: { currentTarget: HTMLVideoElement }) => ensurePlaying(e.currentTarget),
    onSuspend: (e: { currentTarget: HTMLVideoElement }) => ensurePlaying(e.currentTarget),
    style: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      pointerEvents: 'none',
    },
  });
};

// MM:SS 포맷
const formatDuration = (seconds: number): string => {
  if (seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};
